import React, { useMemo, useState } from "react";
import axios from "axios";
import { ArrowUp, Languages, Loader2 } from "lucide-react";
import { useLang } from "@/lib/useLang";
import { getOrCreateVoterId } from "@/lib/voter";
import Analytics from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const cpLength = (s) => [...(s || "")].length;
const TRUNCATE_AT = 220;
const ML_RE = /[\u0d00-\u0d7f]/;

const detectSourceLang = (text) => (ML_RE.test(text || "") ? "ml" : "en");

const VOTED_KEY = "kerala_speaks_upvoted";

function loadUpvotedSet() {
  try {
    const raw = localStorage.getItem(VOTED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveUpvotedSet(s) {
  try {
    localStorage.setItem(VOTED_KEY, JSON.stringify([...s].slice(-2000)));
  } catch { /* quota — ignore */ }
}

function formatRelative(iso, lang) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diffSec = Math.max(0, (Date.now() - d.getTime()) / 1000);
  const isML = lang === "ml";
  if (diffSec < 60) return isML ? "ഇപ്പോൾ" : "just now";
  const min = Math.floor(diffSec / 60);
  if (min < 60) return isML ? `${min} മിനിറ്റ് മുമ്പ്` : `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return isML ? `${hr} മണിക്കൂർ മുമ്പ്` : `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return isML ? `${day} ദിവസം മുമ്പ്` : `${day}d ago`;
  return d.toLocaleDateString(isML ? "ml-IN" : "en-IN", { day: "numeric", month: "short" });
}

export const MessageCard = ({ msg, categoryName, initiallyUpvoted = false, onUpvoted }) => {
  const { t, lang } = useLang();
  const [voterId] = useState(getOrCreateVoterId);
  const [count, setCount] = useState(msg.upvotes || 0);
  const [voted, setVoted] = useState(initiallyUpvoted);
  const [busy, setBusy] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Translation state
  const sourceLang = useMemo(() => detectSourceLang(msg.message), [msg.message]);
  const targetLang = sourceLang === "ml" ? "en" : "ml";
  // Only offer when the message language differs from the UI language
  const canTranslate = sourceLang !== lang;
  const [showOriginal, setShowOriginal] = useState(true);
  const [translation, setTranslation] = useState("");
  const [translating, setTranslating] = useState(false);
  const [translateErr, setTranslateErr] = useState("");

  const handleTranslate = async () => {
    if (translating) return;
    if (translation) {
      setShowOriginal((s) => !s);
      return;
    }
    setTranslating(true);
    setTranslateErr("");
    try {
      const r = await axios.post(`${API}/messages/${msg.id}/translate`, {
        target_lang: targetLang,
        voter_id: voterId,
      });
      setTranslation(r.data?.translation || "");
      setShowOriginal(false);
    } catch (err) {
      setTranslateErr(err?.response?.data?.detail || (lang === "ml" ? "വിവർത്തനം പരാജയപ്പെട്ടു." : "Translation failed."));
    } finally {
      setTranslating(false);
    }
  };

  const len = useMemo(() => cpLength(msg.message), [msg.message]);
  const truncatable = len > TRUNCATE_AT && !expanded;
  const baseText = !showOriginal && translation ? translation : msg.message;
  const baseLen = useMemo(() => cpLength(baseText), [baseText]);
  const displayText = baseLen > TRUNCATE_AT && !expanded
    ? [...baseText].slice(0, TRUNCATE_AT).join("") + "…"
    : baseText;

  const handleUpvote = async () => {
    if (busy) return;
    setBusy(true);
    setVoted(true);                       // optimistic
    setCount((c) => c + 1);
    setPulse(true);
    setTimeout(() => setPulse(false), 600);

    try {
      const r = await axios.post(`${API}/messages/${msg.id}/upvote`, {
        voter_id: voterId,
      });
      // Reconcile with server truth
      if (typeof r.data?.upvotes === "number") setCount(r.data.upvotes);
      // Persist locally
      const set = loadUpvotedSet();
      set.add(msg.id);
      saveUpvotedSet(set);
      Analytics.messageUpvote(msg.id, typeof window !== "undefined" && window.location.pathname.startsWith("/m/") ? "detail" : "feed");
      if (typeof onUpvoted === "function") onUpvoted(msg.id, r.data?.upvotes);
    } catch (err) {
      // Roll back on failure
      setVoted(initiallyUpvoted);
      setCount(msg.upvotes || 0);
      if (err?.response?.status === 429) {
        // Soft-rate-limited; don't roll back further
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      className="brutal-card p-4 md:p-5 fade-up"
      data-testid={`message-card-${msg.id}`}
    >
      <div className="flex items-start gap-3 md:gap-4">
        <button
          type="button"
          onClick={handleUpvote}
          disabled={busy || voted}
          aria-pressed={voted}
          aria-label={t("upvote_aria_label")}
          className="flex flex-col items-center justify-center border-4 border-black px-2 py-2 shrink-0 transition-transform"
          style={{
            background: voted ? "#FF9933" : "#FDFBF7",
            color: "#0A0A0A",
            minWidth: 56,
            transform: pulse ? "scale(1.08)" : "scale(1)",
            transition: "transform 200ms ease-out, background 200ms",
          }}
          data-testid={`upvote-btn-${msg.id}`}
        >
          <ArrowUp size={20} strokeWidth={3} />
          <span className="font-display text-base leading-none mt-1" data-testid={`upvote-count-${msg.id}`}>
            {count}
          </span>
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span
              className="text-[10px] uppercase font-extrabold tracking-[0.2em] px-2 py-0.5 border-2 border-black"
              style={{ background: "#0A0A0A", color: "#FF9933" }}
              data-testid={`category-tag-${msg.id}`}
            >
              {categoryName || msg.category}
            </span>
            {msg.featured && (
              <span
                className="text-[10px] uppercase font-extrabold tracking-[0.2em] px-2 py-0.5 border-2 border-black"
                style={{ background: "#E3000F", color: "#FFFFFF" }}
                data-testid={`featured-tag-${msg.id}`}
              >
                ★ {t("featured_tag")}
              </span>
            )}
            <span className="text-xs font-bold text-neutral-500 ml-auto" data-testid={`time-${msg.id}`}>
              {formatRelative(msg.created_at, lang)}
            </span>
          </div>

          <p
            className="text-base md:text-lg font-medium leading-relaxed whitespace-pre-wrap break-words"
            data-testid={`message-text-${msg.id}`}
            lang={!showOriginal && translation ? targetLang : sourceLang}
          >
            {displayText}
          </p>

          <div className="flex flex-wrap items-center gap-3 mt-2">
            {len > TRUNCATE_AT && (
              <button
                type="button"
                onClick={() => setExpanded((x) => !x)}
                className="text-xs uppercase font-extrabold tracking-[0.25em] underline-offset-2 hover:underline"
                style={{ color: "#0A0A0A" }}
                data-testid={`read-more-${msg.id}`}
              >
                {expanded ? t("read_less") : t("read_more")}
              </button>
            )}

            {canTranslate && (
              <button
                type="button"
                onClick={handleTranslate}
                disabled={translating}
                className="inline-flex items-center gap-1.5 text-xs uppercase font-extrabold tracking-[0.25em] underline-offset-2 hover:underline disabled:opacity-60"
                style={{ color: "#0A0A0A" }}
                data-testid={`translate-btn-${msg.id}`}
                aria-label={`Translate to ${targetLang === "ml" ? "Malayalam" : "English"}`}
              >
                {translating
                  ? <Loader2 size={12} strokeWidth={3} className="animate-spin" />
                  : <Languages size={12} strokeWidth={3} />}
                {translation
                  ? (showOriginal
                      ? (lang === "ml" ? "ഇംഗ്ലീഷിൽ കാണുക" : "See translation")
                      : (lang === "ml" ? "മൂലരൂപം കാണുക" : "See original"))
                  : (translating
                      ? (lang === "ml" ? "വിവർത്തനം ചെയ്യുന്നു…" : "Translating…")
                      : (targetLang === "ml" ? "Translate · മലയാളം" : "Translate · English"))}
              </button>
            )}

            {!showOriginal && translation && (
              <span className="text-[10px] uppercase font-extrabold tracking-[0.3em] text-neutral-500" data-testid={`translation-badge-${msg.id}`}>
                AI translation
              </span>
            )}

            {translateErr && (
              <span className="text-[10px] uppercase font-bold tracking-[0.25em]" style={{ color: "#E3000F" }} data-testid={`translate-err-${msg.id}`}>
                {translateErr}
              </span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default MessageCard;
